// Captura rápida: la nota cae en notes/YYYY-MM-DD-* (inbox estilo gbrain) y el
// grafo la conecta solo. También se puede capturar por WhatsApp con Chief
// ("apunta esto en Tarjeta Presente").

import { useMemo, useState } from 'react'
import { formatDistanceToNow } from 'date-fns'
import { es } from 'date-fns/locale'
import { MessageCircle, StickyNote } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Textarea } from '@/components/ui/textarea'
import { useBrainPages, useBrainWrite } from '@/hooks/useBrain'

interface Props {
  onOpenPage: (slug: string) => void
}

const NO_COMPANY = '__none__'

export function CapturaTab({ onOpenPage }: Props) {
  const [title, setTitle] = useState('')
  const [content, setContent] = useState('')
  const [companySlug, setCompanySlug] = useState<string>(NO_COMPANY)
  const write = useBrainWrite()
  // Dedicated queries: the mixed recency window can drop old companies
  const { data: companyPages } = useBrainPages({ pageType: 'company', limit: 100 })
  const { data: notePages } = useBrainPages({ pageType: 'note', limit: 20 })

  const companies = useMemo(() => companyPages ?? [], [companyPages])
  const notes = useMemo(() => notePages ?? [], [notePages])

  const submit = async () => {
    const text = content.trim()
    if (!text) { toast.error('Escribe la nota primero'); return }
    const withLink = companySlug !== NO_COMPANY ? `${text}\n\n[[${companySlug}]]` : text
    try {
      const res = await write.mutateAsync({
        page_type: 'note',
        title: title.trim() || text.split(/\s+/).slice(0, 8).join(' '),
        content_md: withLink,
        source: 'manual',
      })
      toast.success(`Nota guardada: ${res.slug}`)
      setTitle('')
      setContent('')
      setCompanySlug(NO_COMPANY)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'No se pudo guardar la nota')
    }
  }

  const ago = (iso: string) => formatDistanceToNow(new Date(iso), { addSuffix: true, locale: es })

  return (
    <div className="mx-auto max-w-3xl space-y-5">
      <Card className="space-y-3 p-5">
        <div className="grid gap-1.5">
          <Label htmlFor="tp-note-title">Título (opcional)</Label>
          <Input id="tp-note-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Reunión con Adyen sobre hardware" />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="tp-note-body">Nota *</Label>
          <Textarea
            id="tp-note-body"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={5}
            placeholder={'Markdown ok. Puedes enlazar páginas con [[companies/bancoppel]] — el grafo también detecta menciones solo.'}
          />
        </div>
        <div className="flex items-end justify-between gap-3">
          <div className="grid w-56 gap-1.5">
            <Label>Cuenta relacionada</Label>
            <Select value={companySlug} onValueChange={setCompanySlug}>
              <SelectTrigger><SelectValue placeholder="(ninguna)" /></SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_COMPANY}>(ninguna)</SelectItem>
                {companies.map((c) => (
                  <SelectItem key={c.slug} value={c.slug}>{c.title}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button onClick={submit} disabled={write.isPending}>
            <StickyNote className="mr-1.5 h-3.5 w-3.5" />
            {write.isPending ? 'Guardando…' : 'Guardar en el cerebro'}
          </Button>
        </div>
      </Card>

      <div className="flex items-start gap-2 rounded-lg border border-border/60 bg-muted/30 p-3 text-xs text-muted-foreground">
        <MessageCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <span>
          También por WhatsApp: dile a Chief <em>“apunta esto en Tarjeta Presente: …”</em> y la nota
          cae aquí (skill <span className="font-mono">tp_capture</span>).
        </span>
      </div>

      {notes.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Notas recientes
          </div>
          {notes.map((n) => (
            <Card
              key={n.id}
              className="cursor-pointer p-3 transition-colors hover:bg-muted/50"
              onClick={() => onOpenPage(n.slug)}
            >
              <div className="flex items-center gap-2">
                <span className="truncate font-medium">{n.title}</span>
                <span className="ml-auto shrink-0 text-xs text-muted-foreground">
                  {n.source === 'whatsapp' ? 'WhatsApp · ' : ''}{ago(n.updated_at)}
                </span>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
